import type { MaybeRefOrGetter, Ref } from 'vue'
import type { TeacherArticle } from '~/models/Content/TeacherArticle'
import type { ContentPackage } from '~/models/Content/ContentPackage'
import type { ArticleContent } from '~/models/Content/ArticleContent'
import { computed, ref, toValue } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import useArrayUtils from '~/utils/arrayUtils'
import useAuthStore from '~/stores/auth'
import { ContentType } from '~/models/Content/ContentType'
import { useContentHelper } from '~/composables/useContentHelper'
import useContentApi from '~/api/contentApi'

export default (options: {
  contentId?: MaybeRefOrGetter<number>
  withContent: boolean
  enabled?: Ref<boolean>
  contentTypes?: ContentType[]
}) => {
  const {
    contentId,
    withContent,
    enabled = ref(true),
    contentTypes = [ContentType.TeacherArticle],
  } = options

  const { findContents } = useContentApi()
  const { packageTypes } = useContentHelper()
  const { isTeacher } = storeToRefs(useAuthStore())
  const { truthy } = useArrayUtils()

  const contentIdRef = computed(() => toValue(contentId))

  const {
    data: article,
    isLoading: isLoadingArticle,
    isError: isErrorArticle
  } = useQuery({
    enabled: computed(() => enabled.value && !!contentIdRef.value && isTeacher.value),
    staleTime: Infinity,
    queryKey: computed(() => ['teacher-guide', contentIdRef.value, ...contentTypes]),
    queryFn: () => findContents<TeacherArticle>({
      contentIdCriterion: [contentIdRef.value!],
      contentTypeCriterion: contentTypes,
    }, 1).then(([article]) => article)
  })

  const relationIds = computed(() => article.value?.contents?.destinationContentIds ?? [])

  const {
    data: content,
    isLoading: isLoadingContent,
    isError: isErrorContent
  } = useQuery({
    enabled: computed(() => enabled.value && !!contentIdRef.value && withContent && isTeacher.value && relationIds.value.length > 0),
    staleTime: Infinity,
    queryKey: computed(() => ['teacher-content', contentIdRef.value, ...relationIds.value, ...packageTypes, relationIds.value.length]),
    queryFn: () => findContents<ContentPackage | ArticleContent>({
      contentIdCriterion: relationIds.value,
      contentTypeCriterion: [ContentType.ArticleContent, ...packageTypes],
      mainLocationCriterion: true,
    }, relationIds.value.length),
    select: (contents: Array<ContentPackage | ArticleContent>) => relationIds.value
      .map(relationId => contents.find(({ contentId }) => contentId === Number(relationId)))
      .filter(truthy<ContentPackage | ArticleContent>)
  })

  return {
    teacherArticle: article,
    teacherContent: content,
    isLoadingTeacherArticle: isLoadingArticle,
    isLoadingTeacherContent: isLoadingContent,
    isErrorTeacherArticle: isErrorArticle,
    isErrorTeacherContent: isErrorContent,
  }
}
